// apps/web/src/features/bookings/hooks/useEndTimeOptions.ts
import { useMemo } from 'react';
import type { SlotAvailability, EndTimeOption } from '@lov/shared';

interface UseEndTimeOptionsParams {
  slots: SlotAvailability[];
  selectedStartTime: string | null;
  basePrice: number;
  maxDurationSlots?: number; // Default 8 (4 hours)
}

/**
 * Formats duration in minutes to human-readable string
 */
function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `${mins} min`;
  }
  if (mins === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return `${hours}h ${mins}m`;
}

/**
 * Calculate price based on duration
 * Uses per-slot pricing (30 min slots)
 */
function calculatePrice(basePrice: number, durationMinutes: number): number {
  const slots = durationMinutes / 30;
  return Math.round(basePrice * slots);
}

/**
 * Hook to calculate valid end time options based on selected start time
 * Handles edge cases:
 * - Stops at first unavailable slot
 * - Respects venue closing time
 * - Limits maximum duration
 */
export function useEndTimeOptions({
  slots,
  selectedStartTime,
  basePrice,
  maxDurationSlots = 8, // 4 hours max
}: UseEndTimeOptionsParams): EndTimeOption[] {
  return useMemo(() => {
    if (!selectedStartTime || slots.length === 0) {
      return [];
    }

    const startIndex = slots.findIndex((s) => s.startTime === selectedStartTime);
    if (startIndex === -1) {
      return [];
    }

    const options: EndTimeOption[] = [];

    // Include the selected slot itself through subsequent slots.
    // Using slot.endTime ensures the last slot before closing is included as a valid option.
    for (let i = startIndex; i < slots.length && i < startIndex + maxDurationSlots; i++) {
      const slot = slots[i];

      // Stop at first unavailable slot
      if (!slot.isAvailable) {
        break;
      }

      const durationMinutes = (i - startIndex + 1) * 30;
      const isRecommended = durationMinutes === 60 || durationMinutes === 120; // 1hr or 2hr

      options.push({
        endTime: slot.endTime,
        durationMinutes,
        durationLabel: formatDuration(durationMinutes),
        price: calculatePrice(basePrice, durationMinutes),
        isRecommended,
      });
    }

    return options;
  }, [slots, selectedStartTime, basePrice, maxDurationSlots]);
}
